// Per-route SEO defaults — fallback when no backend override exists.
// The Seo component will merge: backend overrides > this config > hardcoded defaults.

export const SITE = {
  origin: "https://writersspark.net",
  name: "WriterSpark",
  tagline: "Interactive Writing Improvement Platform for Students",
  defaultOgImage: "https://writersspark.net/writespark-logo.png",
  twitter: "",
  organization: {
    name: "WriterSpark",
    email: "info@writersspark.net",
    url: "https://writersspark.net",
    sameAs: [],
  },
};

const COMMON_KEYWORDS =
  "student writing, essay writing app, AI writing feedback, writing intervention, " +
  "argumentative writing, narrative writing, constructed response, " +
  "writing revision, thesis writing help, writing improvement";

export const seoConfig = {
  "/": {
    title: "WriterSpark | AI Writing Coach for Students — Essay, Argumentative & Narrative Writing",
    description:
      "WriterSpark is the interactive AI writing coach that helps middle and high school students improve essays, argumentative writing, narrative writing, and constructed responses with rubric-based feedback.",
    keywords: COMMON_KEYWORDS,
    type: "website",
  },
  "/about": {
    title: "About WriterSpark — Student Writing Improvement Platform",
    description:
      "Learn how WriterSpark coaches students through essay writing, revision, and analytical writing with grade-adapted, rubric-aligned AI feedback.",
    keywords: "about writerspark, student writing platform, writing coach for students",
  },
  "/contact": {
    title: "Contact WriterSpark — Talk to the Writing Coach Team",
    description: "Get in touch with the WriterSpark team for school partnerships, support, and feedback.",
    keywords: "contact writerspark, writing platform support, school writing partnerships",
  },
  "/faq": {
    title: "WriterSpark FAQ — Common Questions about AI Writing Coaching",
    description:
      "Answers about WriterSpark's AI writing coach for students: how feedback works, grade levels supported, SAT/AP prep, pricing, and privacy.",
    keywords: "writerspark faq, AI writing coach questions, student writing platform FAQ",
  },
  "/blog": {
    title: "WriterSpark Blog — Essay, Argumentative & Narrative Writing Strategies",
    description:
      "Practical strategies, lesson ideas, and student writing guides — covering essay writing, argumentative writing, narrative writing, constructed responses, and writing revision.",
    keywords: "student writing blog, essay writing tips, argumentative writing strategies, writing revision guide",
  },
  "/login": {
    title: "Log in to WriterSpark",
    description: "Log in to continue your guided writing practice.",
  },
  "/register": {
    title: "Create your WriterSpark account",
    description:
      "Sign up free — start improving essay writing, argumentative writing, and constructed responses with personalized AI coaching.",
  },
  "/privacy": {
    title: "Privacy Policy — WriterSpark",
    description: "How WriterSpark collects, uses, and protects student writing data.",
  },
  "/terms": {
    title: "Terms of Service — WriterSpark",
    description: "Terms covering use of WriterSpark's student writing improvement platform.",
  },

  // ===== SEO Landing Pages (keyword clusters) =====
  "/essay-writing-help": {
    title: "Essay Writing Help for Students | AI Coaching with Rubric Feedback",
    description:
      "Get step-by-step essay writing help — thesis, evidence, analysis, structure. WriterSpark's AI coach gives rubric-aligned feedback for middle and high school essays.",
    keywords: "essay writing help, essay writing coach, essay writing app, AI essay feedback, student essay help",
    h1: "Essay Writing Help — From Thesis to Final Draft",
    intent: "Learn essay writing with rubric-aligned AI feedback.",
  },
  "/argumentative-writing-support": {
    title: "Argumentative Writing Support for Students | Claim, Evidence, Reasoning Coach",
    description:
      "Master argumentative writing with WriterSpark — coach students through claim, counterclaim, evidence, and reasoning with adaptive AI feedback.",
    keywords: "argumentative writing, argumentative essay help, claim evidence reasoning, CER, argumentative writing platform",
    h1: "Argumentative Writing — Coach Strong Claims, Evidence & Reasoning",
    intent: "Practice argumentative writing the way teachers grade it.",
  },
  "/constructed-response-strategies": {
    title: "Constructed Response Strategies | RACE & ACE Coaching for Students",
    description:
      "Constructed response practice using RACE and ACE strategies with instant AI feedback on text-based evidence, analysis, and explanation.",
    keywords: "constructed response, constructed response strategies, RACE strategy, ACE strategy, short answer writing",
    h1: "Constructed Response — RACE & ACE Coaching",
    intent: "Master the constructed response prompt format used across state tests.",
  },
  "/writing-intervention-tools": {
    title: "Writing Intervention Tools for Schools and Tutors",
    description:
      "Writing intervention tools that diagnose where students struggle — grammar, structure, evidence, clarity — and coach them with grade-adapted feedback.",
    keywords: "writing intervention, writing intervention tools, student writing intervention, writing diagnostic, MTSS writing",
    h1: "Writing Intervention — Diagnose, Coach, Track Growth",
    intent: "Tools for tutors and schools to deliver targeted writing intervention.",
  },
  "/student-writing-improvement": {
    title: "Student Writing Improvement Platform | Grade-Adapted AI Coaching",
    description:
      "An end-to-end student writing improvement platform — daily prompts, rubric scoring, revision intelligence, and growth analytics from elementary through high school.",
    keywords: "student writing improvement, improve student writing, writing growth platform, writing analytics for students",
    h1: "Student Writing Improvement — Daily Practice that Compounds",
    intent: "Build writing skills over time with measurable growth.",
  },
  "/ai-writing-feedback-for-students": {
    title: "AI Writing Feedback for Students | Rubric-Aligned Coaching, Not Auto-Grading",
    description:
      "AI writing feedback designed for student growth — rubric scores, specific revision suggestions, grade-banded tone. Built to coach, never to write for them.",
    keywords: "AI writing feedback, AI writing feedback for students, AI essay feedback, rubric AI feedback, writing AI tutor",
    h1: "AI Writing Feedback Built for Student Growth",
    intent: "Get specific, rubric-aligned AI feedback that teaches — not auto-graded shortcuts.",
  },
  "/narrative-writing-support": {
    title: "Narrative Writing Support for Students | Story, Voice, Pacing Coach",
    description:
      "Narrative writing support that coaches scene, character, dialogue, and pacing with feedback aligned to common rubrics.",
    keywords: "narrative writing, narrative writing support, story writing for students, narrative essay coach",
    h1: "Narrative Writing — Coach Scene, Character & Pacing",
    intent: "Help students write stronger personal and creative narratives.",
  },
  "/thesis-writing-help": {
    title: "Thesis Writing Help | Build Arguable, Specific Thesis Statements",
    description:
      "Thesis writing help that teaches the difference between a topic, a claim, and a defensible thesis — with examples for argumentative and analytical essays.",
    keywords: "thesis writing help, how to write a thesis, thesis statement coach, thesis writing app",
    h1: "Thesis Writing Help — Build Arguable, Specific Thesis Statements",
    intent: "Turn vague claims into defensible thesis statements.",
  },
  "/paragraph-organization-practice": {
    title: "Paragraph Organization Practice | Topic, Evidence, Analysis, Transition",
    description:
      "Paragraph organization practice with TEAR / PEEL / TEEL frameworks — scaffolded so students stop writing one giant block.",
    keywords: "paragraph organization, paragraph structure, TEAR paragraph, PEEL paragraph, body paragraph practice",
    h1: "Paragraph Organization — Topic, Evidence, Analysis, Transition",
    intent: "Practice writing tight body paragraphs that actually argue.",
  },
  "/writing-revision-strategies": {
    title: "Writing Revision Strategies | Draft, Revise, Improve — Tracked Over Time",
    description:
      "Writing revision strategies that show students what to change and why — tracked across drafts so growth is visible.",
    keywords: "writing revision, writing revision strategies, revise essay, draft tracking, revision intelligence",
    h1: "Writing Revision — See Your Writing Grow Draft by Draft",
    intent: "Revise with intent — and watch the rubric scores climb.",
  },
};

export function configFor(path) {
  return seoConfig[path] || {};
}
